/**
 * Reservation modal — captures pre-launch intent for any paid tier.
 *
 * We use this BEFORE we wire real Stripe subscriptions. The user enters
 * their email + (optional) current earnings; we store the row in
 * `pro_reservations` server-side and convert them automatically when
 * paid billing flips on. Zero charge today.
 *
 * Conversion polish (Feb 2026): when the user enters their current monthly
 * earnings on a CLINICIAN paid tier, we show a live, honest Starter-vs-target
 * net-earnings preview right above the Reserve button. The math mirrors
 * `<ClinicianEarningsCalculator/>` exactly (same source of truth: the live
 * `/api/pricing/plans?audience=clinician` payload) so the number they see
 * here matches the number they saw on the marketing page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Loader2, CheckCircle2, Sparkles, TrendingUp, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

export function ReserveProSpotModal({ open, onClose, plan, defaultEmail = '', defaultName = '', discipline = null, source = null }) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [earnings, setEarnings] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [clinicianPlans, setClinicianPlans] = useState([]);

  // Lazy-fetch the full clinician catalog only when this modal is opened
  // against a clinician paid tier — keeps non-clinician surfaces free of
  // a wasted API hit and gives us both the Starter take-rate AND the
  // target plan's monthly fee from a single source of truth.
  useEffect(() => {
    if (!open || plan?.audience !== 'clinician' || clinicianPlans.length > 0) return;
    let cancelled = false;
    axios.get(`${API}/pricing/plans?audience=clinician`)
      .then((r) => {
        if (!cancelled) setClinicianPlans(r.data?.plans || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, plan?.audience, clinicianPlans.length]);

  const earningsNum = useMemo(() => {
    const n = parseFloat(earnings);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [earnings]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/pricing/reservations`, {
        plan_id: plan.id,
        email: email.trim(),
        name: name.trim() || null,
        role: plan.audience,
        current_monthly_earnings_usd: earnings ? parseFloat(earnings) : null,
        discipline: discipline || null,
        source: source || (typeof window !== 'undefined' ? window.location?.pathname : null),
      });
      setDone(true);
    } catch (e1) {
      toast.error(e1?.response?.data?.detail || 'Could not reserve — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setDone(false);
    setEmail(defaultEmail);
    setName(defaultName);
    setEarnings('');
    onClose?.();
  };

  if (!plan) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="reserve-pro-modal">
        {!done ? (
          <>
            <DialogHeader>
              <div className="inline-flex w-12 h-12 rounded-2xl bg-cn-primary/10 text-cn-primary items-center justify-center mb-2">
                <Sparkles className="w-5 h-5" />
              </div>
              <DialogTitle className="text-2xl font-display">Reserve your {plan.name} spot</DialogTitle>
              <p className="text-sm text-cn-text-muted mt-1">
                {plan.audience === 'clinician'
                  ? `${plan.name} is currently in pre-launch. We'll enroll you the moment subscription billing goes live — and you don't pay until the month you cross $${(plan.auto_upgrade_threshold_usd ?? 2000).toLocaleString()} in CaraLoom earnings. No charge today.`
                  : `${plan.name} is currently in pre-launch. We'll enroll you the moment subscription billing goes live. No charge today.`}
              </p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-3">
              <div>
                <Label htmlFor="reserve-email" className="text-xs font-bold uppercase tracking-wider text-cn-text-muted">Email</Label>
                <Input
                  id="reserve-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1.5"
                  data-testid="reserve-email-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="reserve-name" className="text-xs font-bold uppercase tracking-wider text-cn-text-muted">Name (optional)</Label>
                <Input
                  id="reserve-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith, RN"
                  className="mt-1.5"
                  data-testid="reserve-name-input"
                />
              </div>
              {plan.audience === 'clinician' && (
                <div>
                  <Label htmlFor="reserve-earnings" className="text-xs font-bold uppercase tracking-wider text-cn-text-muted">Current monthly earnings (optional)</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cn-text-muted text-sm">$</span>
                    <Input
                      id="reserve-earnings"
                      type="number"
                      value={earnings}
                      onChange={(e) => setEarnings(e.target.value.replace(/[^0-9.]/g, ''))}
                      placeholder="3,200"
                      className="pl-7"
                      data-testid="reserve-earnings-input"
                    />
                  </div>
                  <p className="text-[11px] text-cn-text-muted mt-1.5">Helps us prioritize enrollment for clinicians already running a practice.</p>
                </div>
              )}

              {plan.audience === 'clinician' && earningsNum > 0 && (
                <NetEarningsPreview
                  monthly={earningsNum}
                  targetPlan={plan}
                  clinicianPlans={clinicianPlans}
                />
              )}

              <Button
                type="submit"
                disabled={busy}
                className="cn-btn cn-btn-primary w-full justify-center mt-2"
                data-testid="reserve-submit-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reserve my spot'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="reserve-success">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 items-center justify-center mb-3">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="font-display text-2xl font-bold text-cn-text">You&apos;re on the list.</h2>
            <p className="text-sm text-cn-text-muted mt-2">
              We&apos;ll email you the moment {plan.name} goes live — no charge today, no credit card required.
            </p>
            <Button onClick={close} className="cn-btn cn-btn-ghost mt-5" data-testid="reserve-done-btn">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Live Starter-vs-target net-earnings comparison.
 *
 * Math (mirrors `<ClinicianEarningsCalculator/>`):
 *   - starterNet = monthly * (1 - starter_take_rate)
 *   - targetNet  = monthly * (1 - target_take_rate) - target_monthly_fee
 *   - delta      = targetNet - starterNet
 *   - breakeven  = target_monthly_fee / (starter_take_rate - target_take_rate)
 *
 * Honesty rules:
 *   - When delta ≥ 0 → green "You'd keep $X more / month on {plan.name}"
 *   - When delta < 0 → amber "Pro doesn't pay off yet — breakeven at ≈$Y/mo"
 *   - Falls back to seeded defaults (12% / 8% / $49 / 5% / $149) if the
 *     plans API hasn't returned yet so the user never sees an empty card.
 */
function NetEarningsPreview({ monthly, targetPlan, clinicianPlans }) {
  const starter = clinicianPlans.find((p) => p.id === 'clinician.starter');
  // Resolve target plan from the live catalog if possible (so an admin
  // PUT on the plan reflects here without a redeploy) — fall back to the
  // prop the parent passed in.
  const target = clinicianPlans.find((p) => p.id === targetPlan?.id) || targetPlan;

  const starterTake = (starter?.take_rate_pct ?? 12) / 100;
  const targetTake = (target?.take_rate_pct ?? 8) / 100;
  const targetFee = target?.monthly_price_usd ?? 49;

  const starterNet = monthly * (1 - starterTake);
  const targetNet = monthly * (1 - targetTake) - targetFee;
  const delta = targetNet - starterNet;
  const wins = delta >= 0;

  const takeRateDiff = starterTake - targetTake;
  const breakeven = takeRateDiff > 0 ? targetFee / takeRateDiff : null;

  return (
    <div
      className={`rounded-2xl border-2 p-4 transition-colors ${
        wins
          ? 'border-cn-primary/35 bg-cn-primary/[0.04]'
          : 'border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20'
      }`}
      data-testid="reserve-net-preview"
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
        <Sparkles className="w-3.5 h-3.5 text-cn-primary" />
        Your estimated take-home at {fmt(monthly)}/mo
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3">
        <div data-testid="reserve-net-starter">
          <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">
            Today · Starter
          </div>
          <div className="font-display font-bold text-xl text-cn-text dark:text-white mt-0.5">
            {fmt(starterNet)}
            <span className="text-xs text-cn-text-muted font-normal">/mo</span>
          </div>
          <div className="text-[10px] text-cn-text-muted mt-0.5">
            {(starter?.take_rate_pct ?? 12)}% service rate
          </div>
        </div>
        <div data-testid="reserve-net-plan">
          <div className="text-[10px] uppercase tracking-wider font-bold text-cn-primary">
            With {target?.name || 'Pro'}
          </div>
          <div className="font-display font-bold text-xl text-cn-primary mt-0.5">
            {fmt(targetNet)}
            <span className="text-xs text-cn-text-muted font-normal">/mo</span>
          </div>
          <div className="text-[10px] text-cn-text-muted mt-0.5">
            {(target?.take_rate_pct ?? 8)}% + ${targetFee}/mo
          </div>
        </div>
      </div>

      <div
        className={`mt-3 pt-3 border-t flex items-start gap-1.5 text-xs ${
          wins
            ? 'border-cn-primary/20 text-cn-primary'
            : 'border-amber-300/40 text-amber-800 dark:text-amber-300'
        }`}
        data-testid="reserve-net-delta"
      >
        {wins ? (
          <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        ) : (
          <TrendingDown className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        )}
        <span className="leading-snug">
          {wins ? (
            <>
              You&apos;d keep <strong>{fmt(delta)} more every month</strong> on {target?.name || 'Pro'}
              {' '}— that&apos;s {fmt(delta * 12)}/year vs Starter.
            </>
          ) : (
            <>
              At this volume, Starter still wins by {fmt(Math.abs(delta))}/mo.
              {breakeven ? (
                <> {target?.name || 'Pro'} starts paying off around <strong>{fmt(breakeven)}/mo</strong> in billings — your auto-upgrade prompt fires there too.</>
              ) : null}
            </>
          )}
        </span>
      </div>
    </div>
  );
}

export default ReserveProSpotModal;
